class BestPairWith extends HTMLElement {
  connectedCallback() {
    const expiresAt = BestPairWith.modeExpiresAt?.get(this.dataset.productFormId) || 0;
    this.dataset.mode = expiresAt > Date.now() ? 'add' : 'select';
    this.querySelectorAll('.best-pair-with__input').forEach((input) => {
      input.addEventListener('change', () => input.closest('.best-pair-with__card').classList.toggle('is-selected', input.checked));
    });
    this.addEventListener('click', this.onClick.bind(this));

    BestPairWith.bindSubmitHandler();
    BestPairWith.bindMainAddedHandler();
  }

  async onClick(event) {
    const button = event.target.closest('[data-best-pair-add]');
    if (!button || button.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.setAllStatuses(this.dataset.productFormId, '');
    try {
      const data = await this.addItems([{ id: Number(button.dataset.bestPairAdd), quantity: 1 }]);
      this.renderCart(data, button);
    } catch (error) {
      this.setAllStatuses(this.dataset.productFormId, error.message || 'Unable to add this item to your cart.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async addSelectedPairs(event, form) {
    const formId = form.getAttribute('id');
    if (!formId) return;

    const pairInputs = Array.from(document.querySelectorAll('best-pair-with .best-pair-with__input:checked')).filter(
      (input) => input.closest('best-pair-with')?.dataset.productFormId === formId
    );
    if (!pairInputs.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const mainVariantId = form.querySelector('[name="id"]')?.value;
    if (!mainVariantId) {
      this.setAllStatuses(formId, 'Please select an available variant.');
      return;
    }

    const quantities = new Map();
    const addItem = (id, quantity) => quantities.set(String(id), (quantities.get(String(id)) || 0) + quantity);
    addItem(mainVariantId, Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1));
    pairInputs.forEach((input) => addItem(input.dataset.pairVariantId, 1));

    const submitter = event.submitter || form.querySelector('[name="add"]');
    submitter?.setAttribute('aria-busy', 'true');
    if (submitter) submitter.disabled = true;
    this.setAllStatuses(formId, '');
    try {
      const data = await this.addItems(Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity })));
      BestPairWith.activate(formId);
      this.renderCart(data, submitter);
    } catch (error) {
      this.setAllStatuses(formId, error.message || 'Unable to add this item to your cart.');
    } finally {
      if (submitter) submitter.disabled = false;
      submitter?.removeAttribute('aria-busy');
    }
  }

  async addItems(items) {
    if (window.MonovaGifts?.active()) {
      await window.MonovaGifts.addPlain(items);
      return {};
    }
    const renderer = document.querySelector('cart-drawer, cart-notification');
    const sections = renderer?.getSectionsToRender?.().map((section) => section.id) || [];
    const configuredCartAddUrl = window.routes?.cart_add_url;
    const cartAddUrl = configuredCartAddUrl?.includes('/cart/add') ? configuredCartAddUrl : '/cart/add';
    const response = await fetch(cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || 'Unable to add this item to your cart.');
    return data;
  }

  renderCart(data, activeElement) {
    const renderer = document.querySelector('cart-drawer, cart-notification');
    if (renderer?.renderContents && data.sections) {
      renderer.setActiveElement?.(activeElement);
      renderer.renderContents(data);
    }
  }

  setAllStatuses(formId, message, state = 'error') {
    document.querySelectorAll(`best-pair-with[data-product-form-id="${formId}"] .best-pair-with__status`).forEach((status) => {
      status.textContent = message;
      status.dataset.state = message ? state : '';
    });
  }

  static activate(formId) {
    BestPairWith.modeExpiresAt ||= new Map();
    BestPairWith.modeTimers ||= new Map();
    clearTimeout(BestPairWith.modeTimers.get(formId));
    BestPairWith.modeExpiresAt.set(formId, Date.now() + 60_000);
    BestPairWith.modeTimers.set(formId, setTimeout(() => BestPairWith.reset(formId), 60_000));
    BestPairWith.updateMode(formId, 'add');
  }

  static reset(formId) {
    BestPairWith.modeExpiresAt?.delete(formId);
    BestPairWith.modeTimers?.delete(formId);
    BestPairWith.updateMode(formId, 'select');
  }

  static updateMode(formId, mode) {
    document.querySelectorAll('best-pair-with').forEach((block) => {
      if (block.dataset.productFormId !== formId) return;
      block.dataset.mode = mode;
      if (mode === 'add') {
        block.querySelectorAll('.best-pair-with__input').forEach((input) => {
          input.checked = false;
          input.closest('.best-pair-with__card')?.classList.remove('is-selected');
        });
      }
    });
  }

  static bindMainAddedHandler() {
    if (BestPairWith.mainAddedHandlerBound) return;
    BestPairWith.mainAddedHandlerBound = true;
    document.addEventListener('best-pair-with:main-added', (event) => {
      if (event.detail?.formId) BestPairWith.activate(event.detail.formId);
    });
    window.addEventListener('pageshow', () => {
      BestPairWith.modeExpiresAt?.forEach((expiresAt, formId) => {
        if (expiresAt <= Date.now()) BestPairWith.reset(formId);
      });
    });
  }

  static bindSubmitHandler() {
    if (BestPairWith.submitHandlerBound) return;

    BestPairWith.submitHandlerBound = true;
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
      if (!formId) return;

      const pairBlock = Array.from(document.querySelectorAll('best-pair-with')).find(
        (block) => block.dataset.productFormId === formId
      );
      const section = form.closest('product-form')?.dataset.section;
      if (form.querySelector('monova-gift-wrap[data-has-gift]') || (section && document.querySelector(`monova-gift-wrap[data-context="product"][data-section="${CSS.escape(section)}"][data-has-gift]`))) return;
      if (pairBlock) pairBlock.addSelectedPairs(event, form);
    }, true);
  }
}

if (!customElements.get('best-pair-with')) customElements.define('best-pair-with', BestPairWith);
